import { createStyles, IconButton, makeStyles } from "@material-ui/core";
import classnames from "classnames";
import * as React from "react";

import { TimesIcon } from "../../assets/icons";
import { mergeClasses, RelodeTheme } from "../styles";
import { NotificationIcon } from "./notificationIcon";
import { NotificationLevel, NotificationMessage } from "./types";
import { useNotifications } from "./useNotifications";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                padding: "0 16px",
                fontSize: 16,
                marginBottom: 8,
            },
            icon: {
                paddingRight: 16,
                fontSize: 40,
            },
            message: {
                flexGrow: 1,
                marginTop: 8,
                marginBottom: 8,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
            },
            close: {
                justifySelf: "flex-end",
                color: "gray",
            },
            error: {
                backgroundColor: "#FE69671a",
                border: "1px solid #FE6967",
                borderRadius: "4px",
            },
            info: {
                backgroundColor: "#A6D3F41a",
                border: "1px solid #A6D3F4",
                borderRadius: "4px",
            },
            warning: {
                backgroundColor: "#FF991F1a",
                border: "1px solid #FF991F",
                borderRadius: "4px",
            },
            success: {
                backgroundColor: "#6EEDB81a",
                border: "1px solid #6EEDB8",
                borderRadius: "4px",
            },
        }),
    { name: "AreaNotification" }
);

interface AreaNotificationProps {
    areaName: string;
    className?: string;
    classes?: Partial<ReturnType<typeof useStyles>>;
}

export const AreaNotification = (props: AreaNotificationProps) => {
    const { areaName, className, classes: classesProp } = props;
    const classes = mergeClasses(useStyles({}), classesProp);
    const previous = React.useRef<NotificationMessage>({
        level: "info",
        title: "",
        message: "",
        type: "area",
    });

    const {
        state: { messages },
    } = useNotifications();
    const notification = messages.find(m => m.area === areaName);

    React.useEffect(() => {
        if (!notification) return;
        previous.current = notification;
    }, [notification]);

    const rootClasses = classnames(
        {
            [classes.root]: true,
            [classes[
                notification ? notification.level : previous.current.level
            ]]: true,
        },
        className
    );

    return !!notification ? (
        <AreaNotificationMessage
            level={notification?.level}
            canClose={notification.canClose}
            callback={notification.callback}
            className={className}
            classes={classesProp}
            customIcon={notification.customIcon}
        >
            {notification.message}
        </AreaNotificationMessage>
    ) : null;
};

type AreaNotificationMessageProps = {
    className?: string;
    classes?: Partial<ReturnType<typeof useStyles>>;
    children: React.ReactNode;
    canClose?: boolean;
    customIcon?: React.ReactElement<any>;
    level: NotificationLevel;
    callback?: (key?: string) => void;
};
export const AreaNotificationMessage = (
    props: AreaNotificationMessageProps
) => {
    const {
        children,
        className,
        classes: classesProp,
        canClose = false,
        customIcon,
        level,
        callback,
    } = props;

    const classes = mergeClasses(useStyles({}), classesProp);
    const rootClasses = classnames(
        {
            [classes.root]: true,
            [classes[level]]: true,
        },
        className
    );

    return (
        <>
            <div className={rootClasses}>
                <NotificationIcon
                    level={level}
                    size="small"
                    className={classes.icon}
                    customIcon={customIcon}
                />
                <div className={classes.message}>{children}</div>
                {canClose ? (
                    <IconButton
                        className={classes.close}
                        onClick={() => callback()}
                        color="primary"
                    >
                        <TimesIcon fontSize="small" />
                    </IconButton>
                ) : null}
            </div>
        </>
    );
};
